import React from 'react';

interface SegmentedChipsProps<T extends string> {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  label?: string;
  disabled?: boolean;
}

/**
 * SegmentedChips — segmented control used for the likert `3 / 5 / 10` scale
 * selector. Uses the same purple selected-state vocabulary as AnswerPill.
 */
export default function SegmentedChips<T extends string>({
  options,
  value,
  onChange,
  label,
  disabled,
}: SegmentedChipsProps<T>) {
  return (
    <div>
      {label && (
        <p className="mb-1.5 ml-1 font-sans font-medium text-[13px] leading-5 text-text-secondary">
          {label}
        </p>
      )}
      <div
        role="radiogroup"
        className="inline-flex gap-0.5 p-[3px] rounded-[50px] bg-primary-500/10 border border-primary-500/20"
      >
        {options.map((opt) => {
          const selected = opt.value === value;
          const chipClass = selected
            ? 'bg-primary-500 text-white font-bold'
            : 'bg-transparent text-primary-500 font-medium';
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              className={`min-w-10 px-3.5 py-1.5 rounded-[50px] border-0 font-sans text-[13px] leading-[18px] transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer ${chipClass}`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
